// TabBuilder.jsx (modal via portal, with CSS import and strong z-index)
import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "./css/TabBuilder.css";

const TabBuilder = ({ onClose, onSave, initial }) => {
    const [name, setName] = useState(initial?.name || "");

    // Stage 1: MUST contain
    const [includeWords, setIncludeWords] = useState((initial?.rules?.includeWords || []).join(", "));
    const [includeWholeWords, setIncludeWholeWords] = useState((initial?.rules?.includeWholeWords || []).join(", "));
    const [includePhrases, setIncludePhrases] = useState((initial?.rules?.includePhrases || []).join(", ")); // phrases/regex

    // Stage 2: MUST NOT contain
    const [excludeWords, setExcludeWords] = useState((initial?.rules?.excludeWords || []).join(", "));
    const [excludeWholeWords, setExcludeWholeWords] = useState((initial?.rules?.excludeWholeWords || []).join(", "));
    const [excludePhrases, setExcludePhrases] = useState((initial?.rules?.excludePhrases || []).join(", ")); // phrases/regex

    // Fields to scan
    const [fields, setFields] = useState(initial?.rules?.fields || ["subject", "summary"]);

    // Stage 3: FORCE include (rescue only if excluded after stage 2)
    const [forceIncludePhrases, setForceIncludePhrases] =
        useState((initial?.rules?.forceIncludePhrases || []).join(", ")); // phrases/regex
    const [forceIncludeWords, setForceIncludeWords] =
        useState((initial?.rules?.forceIncludeWords || []).join(", "));
    const [forceIncludeWholeWords, setForceIncludeWholeWords] =
        useState((initial?.rules?.forceIncludeWholeWords || []).join(", "));

    // Lock body scroll while modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    // Close on ESC
    const onKeyDown = useCallback((e) => {
        if (e.key === "Escape") onClose?.();
    }, [onClose]);

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onKeyDown]);

    const toggleField = (f) => {
        setFields((prev) => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
    };

    const parseList = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);

    const handleSave = () => {
        if (!name.trim()) return;
        const rules = {
            // Stage 1: MUST contain
            includeWords: parseList(includeWords.toLowerCase()),
            includeWholeWords: parseList(includeWholeWords.toLowerCase()),
            includePhrases: parseList(includePhrases), // DO NOT lowercase: regex-friendly

            // Stage 2: MUST NOT contain
            excludeWords: parseList(excludeWords.toLowerCase()),
            excludeWholeWords: parseList(excludeWholeWords.toLowerCase()),
            excludePhrases: parseList(excludePhrases), // DO NOT lowercase: regex-friendly

            // Fields
            fields: fields.length ? fields : ["subject","summary"],

            // Stage 3: FORCE include
            forceIncludePhrases: parseList(forceIncludePhrases), // DO NOT lowercase: regex-friendly
            forceIncludeWords: parseList(forceIncludeWords.toLowerCase()),
            forceIncludeWholeWords: parseList(forceIncludeWholeWords.toLowerCase()),
        };
        onSave?.({ name: name.trim(), rules });
    };

    const modal = (
        <div className="tabbuilder-modal" role="dialog" aria-modal="true" aria-label={initial ? "Edit Tab" : "New Tab"}>
            <div className="tabbuilder-backdrop" onClick={onClose} />
            <div className="tabbuilder-card" onClick={(e) => e.stopPropagation()}>
                <div className="tabbuilder-header">
                    <h3>{initial ? "Edit Tab" : "New Tab"}</h3>
                    <button className="tabbuilder-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="tabbuilder-body">
                    <label>
                        Tab Name
                        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Orders" />
                    </label>

                    {/* ===== Stage 1: MUST contain ===== */}
                    <div className="tb-row">
                        <label>
                            Must contain word(s) (substring, ANY)
                            <input value={includeWords} onChange={(e) => setIncludeWords(e.target.value)} placeholder="e.g. transcript, ipo" />
                        </label>
                    </div>

                    <div className="tb-row">
                        <label>
                            Must contain WHOLE word(s) (ANY)
                            <input value={includeWholeWords} onChange={(e) => setIncludeWholeWords(e.target.value)} placeholder="whole words, comma-separated" />
                        </label>
                    </div>

                    <div className="tb-row">
                        <label>
                            MUST contain phrase(s) / regex (ANY)
                            <input value={includePhrases} onChange={(e) => setIncludePhrases(e.target.value)} placeholder='regex allowed, e.g. "conference call", "annual.*general meeting \\d+"' />
                        </label>
                    </div>

                    {/* ===== Stage 2: MUST NOT contain ===== */}
                    <div className="tb-row">
                        <label>
                            MUST NOT contain word(s) (substring, ANY)
                            <input value={excludeWords} onChange={(e) => setExcludeWords(e.target.value)} placeholder="comma-separated" />
                        </label>
                    </div>

                    <div className="tb-row">
                        <label>
                            MUST NOT contain WHOLE word(s) (ANY)
                            <input value={excludeWholeWords} onChange={(e) => setExcludeWholeWords(e.target.value)} placeholder="whole words, comma-separated" />
                        </label>
                    </div>

                    <div className="tb-row">
                        <label>
                            MUST NOT contain phrase(s) / regex (ANY)
                            <input value={excludePhrases} onChange={(e) => setExcludePhrases(e.target.value)} placeholder='regex allowed, e.g. "annual.*general meeting \\d+"' />
                        </label>
                    </div>

                    {/* ===== Stage 3: FORCE include (rescue) ===== */}
                    <hr className="tb-sep" />
                    <div className="tb-row">
                        <label>
                            FORCE include if contains phrase(s) / regex (ANY)
                            <input
                                value={forceIncludePhrases}
                                onChange={(e) => setForceIncludePhrases(e.target.value)}
                                placeholder='regex allowed, e.g. "(investor meet|conference call)"'
                            />
                        </label>
                    </div>

                    <div className="tb-row">
                        <label>
                            FORCE include if contains word(s) (substring, ANY)
                            <input
                                value={forceIncludeWords}
                                onChange={(e) => setForceIncludeWords(e.target.value)}
                                placeholder="comma-separated words"
                            />
                        </label>
                    </div>

                    <div className="tb-row">
                        <label>
                            FORCE include if contains WHOLE word(s) (ANY)
                            <input
                                value={forceIncludeWholeWords}
                                onChange={(e) => setForceIncludeWholeWords(e.target.value)}
                                placeholder="whole words, comma-separated"
                            />
                        </label>
                    </div>

                    <div className="tb-row">
                        <div>Apply to fields:</div>
                        <label><input type="checkbox" checked={fields.includes("subject")} onChange={() => toggleField("subject")} /> Subject</label>
                        <label><input type="checkbox" checked={fields.includes("summary")} onChange={() => toggleField("summary")} /> AI Summary</label>
                        <label><input type="checkbox" checked={fields.includes("company")} onChange={() => toggleField("company")} /> Company</label>
                    </div>
                </div>

                <div className="tabbuilder-actions">
                    <button onClick={handleSave} className="btn-primary">Save</button>
                    <button onClick={onClose} className="btn-secondary">Cancel</button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default TabBuilder;
